import { SelectChangeEvent } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ApplicationProps } from '@/models/Application';
import { ContactProps, ContactType, localizedTextEquals } from '@/models/Contact';
import { ProgramNames } from '@/models/Program';
import { ReviewerTypes } from '@/models/ReviewerAssignment';

type GroupedOptions = Array<[group: string, contacts: ContactProps[]]>;

type UseShippingContactsProps = {
  currentApplication: ApplicationProps;

  // from Config + SelectedProgramContext
  contactTypes: ContactType[];
  selectedProgramId: ProgramNames;

  // from ContactsContext
  currentContacts: ContactProps[] | null;

  // hooks
  getContact: (id: number) => Promise<ContactProps>;
  saveContact: (contact: ContactProps) => Promise<ContactProps>;
};

type UseShippingContactsResult = {
  shippingContactsNeeded: boolean;
  shippingContactType: ContactType | undefined;

  selectedContactId: number | null;
  setSelectedContactId: (id: number | null) => void;
  handleSelectChange: (e: SelectChangeEvent<string>) => void;

  applicantShippingContact: ContactProps | null;
  selectedShippingContact: ContactProps | null;

  groupedOptions: GroupedOptions;

  // for approval hook
  isValidShippingSelection: boolean;
  ensureShippingContactId: () => Promise<number | null>;

  // optional exposure (useful for debugging/UI)
  savedShippingContact: ContactProps | null;
};

export function useShippingContacts({
  currentApplication,
  contactTypes,
  selectedProgramId,
  currentContacts,
  getContact,
  saveContact,
}: UseShippingContactsProps): UseShippingContactsResult {
  const [selectedContactId, setSelectedContactId] = useState<number | null>(null);
  const [savedShippingContact, setSavedShippingContact] = useState<ContactProps | null>(null);
  const [draftShippingContact, setDraftShippingContact] = useState<ContactProps | null>(null);

  useEffect(() => {
    setSavedShippingContact(null);
    setDraftShippingContact(null);
    setSelectedContactId(null);
  }, [currentApplication.id]);

  // Tree Cities of the World doesn't need shipping contact info
  const shippingContactsNeeded = useMemo(() => {
    return currentApplication.programId !== ProgramNames.TreeCitiesWorld;
  }, [currentApplication]);

  const shippingContactType = useMemo(
    () =>
      contactTypes.find(
        (c) =>
          localizedTextEquals(c.contactTypeName, 'Shipping Address') &&
          c.programId === selectedProgramId
      ),
    [contactTypes, selectedProgramId]
  );

  const applicantShippingContact: ContactProps | null = useMemo(() => {
    if (currentContacts == null || shippingContactType == null) return null;

    return (
      currentContacts.find((contact) =>
        (contact.contactRelationships ?? []).some(
          (relationship) =>
            relationship.applicationId === currentApplication.id &&
            relationship.contactTypeId === shippingContactType.id
        )
      ) ?? null
    );
  }, [currentApplication.id, currentContacts, shippingContactType]);

  const { stateCoordinators, regionalCoordinators } = useMemo(() => {
    const state: ContactProps[] = [];
    const regional: ContactProps[] = [];

    if (!shippingContactsNeeded) {
      return { stateCoordinators: state, regionalCoordinators: regional };
    }

    for (const rc of currentApplication.eligibleReviewerContacts ?? []) {
      const list =
        rc.reviewerType === ReviewerTypes.State
          ? state
          : rc.reviewerType === ReviewerTypes.Regional
            ? regional
            : null;

      if (list == null) continue;
      if (!list.some((c) => c.id === rc.contact.id)) list.push(rc.contact);
    }

    const byLast = (a: ContactProps, b: ContactProps) =>
      (a.lastName ?? '').localeCompare(b.lastName ?? '');

    state.sort(byLast);
    regional.sort(byLast);

    return { stateCoordinators: state, regionalCoordinators: regional };
  }, [currentApplication, shippingContactsNeeded]);

  const groupedOptions: GroupedOptions = useMemo(() => {
    const groups = new Map<string, ContactProps[]>();

    const add = (group: string, contact?: ContactProps | null) => {
      if (contact == null) return;
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group)!.push(contact);
    };

    add('Application Default Shipping Address', applicantShippingContact);

    const savedIsInLists =
      savedShippingContact != null &&
      (stateCoordinators.some((c) => c.id === savedShippingContact.id) ||
        regionalCoordinators.some((c) => c.id === savedShippingContact.id) ||
        applicantShippingContact?.id === savedShippingContact.id);

    if (savedShippingContact != null && !savedIsInLists) {
      add('Saved Address', savedShippingContact);
    }

    if (stateCoordinators.length > 0)
      stateCoordinators.forEach((c) => add('State Coordinators', c));
    if (regionalCoordinators.length > 0)
      regionalCoordinators.forEach((c) => add('Regional Coordinators', c));

    return Array.from(groups.entries());
  }, [applicantShippingContact, savedShippingContact, stateCoordinators, regionalCoordinators]);

  const selectedShippingContact = useMemo(() => {
    if (selectedContactId == null) return null;
    if (selectedContactId === applicantShippingContact?.id) return applicantShippingContact;

    return (
      stateCoordinators.find((c) => c.id === selectedContactId) ??
      regionalCoordinators.find((c) => c.id === selectedContactId) ??
      (savedShippingContact?.id === selectedContactId ? savedShippingContact : null) ??
      null
    );
  }, [
    selectedContactId,
    applicantShippingContact,
    stateCoordinators,
    regionalCoordinators,
    savedShippingContact,
  ]);

  // Initialize selection when app loads/changes
  useEffect(() => {
    if (!shippingContactsNeeded || currentApplication == null) return;

    setSelectedContactId(
      currentApplication.shippingContactId ?? applicantShippingContact?.id ?? null
    );
  }, [shippingContactsNeeded, currentApplication, applicantShippingContact?.id]);

  // Load "saved" shipping contact if it exists but isn't in eligible list; otherwise build a DRAFT (no save).
  useEffect(() => {
    let cancelled = false;

    if (
      !shippingContactsNeeded ||
      savedShippingContact != null ||
      currentApplication.partnerId == null
    )
      return;

    const run = async () => {
      const shippingId = currentApplication.shippingContactId;

      const isInEligible = !!(
        shippingId != null &&
        currentApplication.eligibleReviewerContacts?.some(
          (x) => String(x.contact.id) === String(shippingId)
        )
      );

      if (shippingId != null && !isInEligible) {
        const c = await getContact(shippingId);
        if (!cancelled) setSavedShippingContact(c);
        return;
      }

      const appContactId = currentApplication.applicationContactId;
      if (appContactId != null) {
        const c = await getContact(appContactId);

        const draft: ContactProps = {
          streetAddress1: c?.streetAddress1,
          streetAddress2: c?.streetAddress2,
          city: c?.city,
          stateId: c?.stateId,
          countyId: c?.countyId,
          countryId: c?.countryId,
          zipCode: c?.zipCode,
          firstName: c?.firstName,
          lastName: c?.lastName,
          email: c?.email,
          phone: c?.phone,
          contactRelationships: c?.contactRelationships ?? [],
        };

        if (!cancelled) setDraftShippingContact(draft);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [
    currentApplication.applicationContactId,
    currentApplication.eligibleReviewerContacts,
    currentApplication.partnerId,
    currentApplication.shippingContactId,
    getContact,
    savedShippingContact,
    shippingContactsNeeded,
  ]);

  const handleSelectChange = useCallback((e: SelectChangeEvent<string>) => {
    const value = e.target.value;
    setSelectedContactId(value === '' ? null : Number(value));
  }, []);

  const isValidShippingSelection = useMemo(() => {
    if (!shippingContactsNeeded) return true;
    if (selectedContactId == null) return false;

    const id = selectedContactId;

    return (
      id === applicantShippingContact?.id ||
      id === savedShippingContact?.id ||
      stateCoordinators.some((c) => c.id === id) ||
      regionalCoordinators.some((c) => c.id === id)
    );
  }, [
    applicantShippingContact?.id,
    regionalCoordinators,
    savedShippingContact?.id,
    selectedContactId,
    shippingContactsNeeded,
    stateCoordinators,
  ]);

  // Save only when approving (never on render)
  const ensureShippingContactId = useCallback(async (): Promise<number | null> => {
    if (!shippingContactsNeeded) return null;
    if (selectedContactId != null) return selectedContactId;

    // defaulting behavior
    if (savedShippingContact?.id) return savedShippingContact.id;

    // create a real contact only at the last moment
    if (draftShippingContact) {
      const created = await saveContact(draftShippingContact);
      setSavedShippingContact(created);
      return created?.id ?? null;
    }

    return null;
  }, [
    shippingContactsNeeded,
    selectedContactId,
    savedShippingContact?.id,
    draftShippingContact,
    saveContact,
  ]);

  return {
    shippingContactsNeeded,
    shippingContactType,
    selectedContactId,
    applicantShippingContact,
    selectedShippingContact,
    groupedOptions,
    isValidShippingSelection,
    savedShippingContact,
    setSelectedContactId,
    handleSelectChange,
    ensureShippingContactId,
  };
}
